import * as Sentry from "@sentry/react";
import axios from "axios";

import {
  extractCustomMetadataValues,
  type InterviewMetadataState,
} from "../../Pages/ConcludeV2/Metadata/concludeMetadataUtils";
import type { RootState } from "../state/rootReducer";
import type { File as StoryFile } from "../reducers/uploadFilesReducer";

import { notifyError, notifySuccess } from "./notify";
import {
  PROJECT_UPLOADING_LOCAL_FILES,
  PROJECT_UPLOADING_SET_FILES,
  PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
  PROJECT_UPLOADING_SET_COMPLETE_LOCAL_FILES,
  PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
  PROJECT_UPLOADING_DELETE_FILES,
  PROJECT_UPLOADING_BULK_UPDATE_FILES,
  PROJECT_UPLOADING_HYDRATE_METADATA,
  PROJECT_SET_NEED_TO_DELETE_FLAG,
  GET_UPDATE_FILES,
  ADD_FILE,
} from "./types";

export const setLocalFilesToUpload = (filesToUpload, projectId, foldersId, folderId?: string) => async (dispatch) => {
  dispatch({
    type: PROJECT_UPLOADING_SET_FILES,
    payload: { filesToUpload, projectId, total: filesToUpload.length, folders: foldersId, folderId },
  });
  dispatch(uploadLocalFiles());
};

export const uploadLocalFiles = () => async (dispatch, getState) => {
  const { stories: storiesToGetPresignedUrls } = getState().uploadFiles;

  storiesToGetPresignedUrls.sort((a, b) => a._id.localeCompare(b._id));
  // first go through all the files and start the upload process
  const completionPromises = [];
  for (const story of storiesToGetPresignedUrls) {
    completionPromises.push(await dispatch(uploadFileParts(story)));
  }

  // Wait for all local completion checks (thumbnail polling) to settle before reporting the result
  await Promise.all(completionPromises);

  const { stories: storiesAfterUpload } = getState().uploadFiles;
  const completedStories = storiesAfterUpload.filter((story) => story.complete);
  dispatch(notifySuccess(`${completedStories.length} files have been uploaded!`));
};

export const uploadFileParts = (storyPresigned) => async (dispatch, getState) => {
  const { stories, projectId, folders } = getState().uploadFiles;
  const existStoryInStore = stories.find((story) => story.storyId === storyPresigned.storyId);
  const userFlags = (getState() as RootState).featureFlags?.flags;

  // Only proceed if the file still exists in our store
  if (existStoryInStore) {
    const FILECHUNKSIZE = 50 * 1024 * 1024;

    // Get presigned upload configuration from the backend using axios
    const res = await axios.get(
      `/stories/${storyPresigned.storyId}/upload-config` +
        `?type=${storyPresigned.mediaType}&numUploadParts=${Math.ceil(storyPresigned.file.size / FILECHUNKSIZE)}`
    );
    const multipartUploadConfig = res.data;
    const uploadPartURLs = multipartUploadConfig.Parts as string[];

    // Use AbortController to allow cancellation if needed
    const abortController = new AbortController();

    // Flag to mark cancellation
    let shouldCancelUpload = false;

    try {
      // Upload each part sequentially
      await uploadPartURLs.reduce(async (agg, partURL, partIndex) => {
        await agg;

        if (shouldCancelUpload || !storyPresigned) {
          throw new Error("Upload process was cancelled");
        }

        const start = partIndex * FILECHUNKSIZE;
        const end = (partIndex + 1) * FILECHUNKSIZE;
        const blob =
          end < storyPresigned.file.size ? storyPresigned.file.slice(start, end) : storyPresigned.file.slice(start);

        /**
         * NOTE: Direct XMLHttpRequest implementation required due to axios 1.8.3+ compatibility issues
         *
         * After the axios 1.8.3 update, the multipart upload process was failing due to changes in how
         * axios handles PUT requests with binary data and progress tracking. Specifically:
         * 1. PUT requests with binary data weren't being properly formatted AND the upload was not working duo to 401 Unauthorized headers
         * 1.a Despite all the errorts a default header was always added that resulted in mismatching and hence the crashes
         * 2. AbortController integration was not working consistently
         *
         * Solution: Implement direct XMLHttpRequest to:
         * - Maintain reliable upload progress tracking
         * - Ensure proper binary data handling
         * - Keep consistent abort functionality
         * - Guarantee backward compatibility
         */
        const uploadPromise = new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              dispatch(
                uploadProgressIndicator(
                  storyPresigned._id,
                  event.loaded + partIndex * FILECHUNKSIZE,
                  storyPresigned.file.size,
                  abortController
                )
              );
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(xhr.response);
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
            }
          };

          xhr.onabort = () => {
            shouldCancelUpload = true;
            reject(new Error("Upload cancelled"));
          };

          xhr.onerror = () => {
            reject(new Error("Network error occurred"));
          };

          xhr.open("PUT", partURL);

          // Handle abort signal
          if (abortController.signal) {
            abortController.signal.addEventListener("abort", () => {
              shouldCancelUpload = true;
              xhr.abort();
            });
          }

          xhr.send(blob);
        });

        try {
          await uploadPromise;
        } catch (error) {
          if (shouldCancelUpload) {
            throw new Error("Upload process was cancelled");
          }
          throw error;
        }
      }, Promise.resolve());

      // Notify backend to complete the multipart upload
      const completeRes = await axios.post(
        `/stories/${storyPresigned.storyId}/media/upload/completion`,
        {
          key: multipartUploadConfig.Key,
          uploadId: multipartUploadConfig.UploadId,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      console.log("Response Headers /media/upload/completion:", completeRes.headers);

      // Finalize story creation
      const concludeRes = await axios.post(`/stories/${storyPresigned.storyId}/conclude`, {
        notes: "",
        story_id: storyPresigned.storyId,
        groups: [],
        projects: [],
        project: projectId,
        folders: folders,
        emails: [],
        notify_group: false,
        termsAccepted: false,
        is_upload: true,
        mediaKey: res.data.Key, // using the key from the original presigned config response
        mediaType: storyPresigned.mediaType,
        export_to_permanent_org: false,
        file_name: storyPresigned.file.name.split(".").slice(0, -1).join("."),
      });
      console.log("Response Headers /conclude:", concludeRes.headers);

      const isFeatureFlagAutoTranscode = userFlags?.automaticTranscode ?? false;
      console.log("Determine automatic transcription: ", isFeatureFlagAutoTranscode);

      // Call transcription endpoint based on feature flags
      if (isFeatureFlagAutoTranscode) {
        await axios.post(`/stories/${storyPresigned.storyId}/transcode`);
      } else {
        await axios.post(`/stories/${storyPresigned.storyId}/deepgram_transcribe`);
      }

      // Return the promise (without awaiting) so the caller can move on to the next
      // file's upload without blocking on the thumbnail-completion check below.
      return dispatch(completeLocalUpload(storyPresigned.storyId));
    } catch (error) {
      console.error("Upload failed:", error);
      // If cancellation was triggered, remove the story from the store
      if (shouldCancelUpload) {
        dispatch(removeStoryFromStore(storyPresigned.storyId));
      } else {
        dispatch(failedLocalUpload(storyPresigned.storyId));
      }
      return undefined;
    }
  }
  return undefined;
};

export const uploadProgressIndicator = (storyId, uploadedSoFar, fileSize, source) => async (dispatch, getState) => {
  const { stories, filesCompleted } = getState().uploadFiles;
  const storyToUpdateProgress = stories.find((story) => story.storyId === storyId);

  // before we update the progress we need to check if the files need to be removed
  if (storyToUpdateProgress.needToDelete && source && !source.signal.aborted) {
    source.abort();
  }

  const uploadProgress = `${Math.max(1, Math.round((uploadedSoFar / fileSize) * 100))}%`;
  const uploadedPartCount = (storyToUpdateProgress.uploadedPartCount as number) + 1;
  const updatedStory = { ...storyToUpdateProgress, uploadProgress, uploadedPartCount };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));
  const uploadedFraction = Math.max(1, (uploadedSoFar / fileSize) * 100) / 100;

  dispatch({
    type: PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
    payload: {
      updatedStories,
      current: uploadedFraction === 1 ? 0 : uploadedFraction,
      filesCompleted: uploadedFraction === 1 ? filesCompleted + 1 : filesCompleted,
    },
  });
};

export const completeLocalUpload = (storyId) => async (dispatch, getState) => {
  const { stories: initialStories } = getState().uploadFiles;
  const story = initialStories.find((s) => s.storyId === storyId || s._id === storyId);
  const isAudio = story?.mediaType?.startsWith("audio") ?? false;

  // Audio files don't have thumbnails, so we don't need to wait 8 seconds for processing
  const waitTime = isAudio ? 4000 : 8000;

  await new Promise((resolve) => {
    setTimeout(resolve, waitTime);
  });
  const res = await axios.get(`/stories/${storyId}`);

  const { stories } = getState().uploadFiles; // Fetch fresh state to avoid race conditions during bulk uploads
  const storyToUpdateProgress = stories.find((s) => s.storyId === storyId || s._id === storyId);

  if (!storyToUpdateProgress) return;

  const uploadProgress = "100%";
  const complete = true;
  const updatedStory = {
    ...storyToUpdateProgress,
    _id: res.data._id,
    uploadProgress,
    complete,
    thumbnailURL: res.data.thumbnailURL,
  };
  const restOfStories = stories.filter((s) => (s.storyId || s._id) !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  const areLocalStoriesCompleted = updatedStories.every((s) => s.complete);
  updatedStories.sort((a, b) => (a._id || a.storyId || "").localeCompare(b._id || b.storyId || ""));
  dispatch({
    type: PROJECT_UPLOADING_SET_COMPLETE_LOCAL_FILES,
    payload: {
      updatedStories,
      uploadingFiles: !areLocalStoriesCompleted,
    },
  });
};

export const failedLocalUpload = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;
  const storyToUpdateProgress = stories.find((story) => story.storyId === storyId);
  const uploadProgress = "FAILED";
  const complete = false;
  const updatedStory = { ...storyToUpdateProgress, uploadProgress, complete };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));

  dispatch({
    type: PROJECT_UPLOADING_SET_PROGRESS_LOCAL_FILES,
    payload: {
      updatedStories,
    },
  });
};

export const addFlagToDeleteLocalFile = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;
  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const updatedStory = { ...fileToDelete, needToDelete: true };
  const restOfStories = stories.filter((story) => story.storyId !== storyId);
  const updatedStories = [...restOfStories, updatedStory];
  updatedStories.sort((a, b) => a._id.localeCompare(b._id));

  dispatch({
    type: PROJECT_SET_NEED_TO_DELETE_FLAG,
    payload: {
      updatedStories: updatedStories,
    },
  });
};

export const removeStoryFromStore = (storyId) => async (dispatch, getState) => {
  const { stories, total, filesCompleted, current } = getState().uploadFiles;
  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const filteredFiles = stories.filter((story) => story.storyId !== storyId);
  const areStoriesRemaining = filteredFiles.length > 0;

  dispatch({
    type: PROJECT_UPLOADING_DELETE_FILES,
    payload: {
      updatedStories: filteredFiles,
      total: total - 1,
      current: fileToDelete.complete ? current : 0,
      filesCompleted: fileToDelete.complete ? filesCompleted - 1 : filesCompleted,
      uploadingFiles: areStoriesRemaining,
    },
  });
};

export const deleteFileUpload = (storyId) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;

  const fileToDelete = stories.find((story) => story.storyId === storyId);
  const isComplete = fileToDelete.complete;
  const isNotStarted = fileToDelete.uploadedPartCount === 0 && !fileToDelete.complete;

  // if the story is already upload we delete and remove it from redux
  if (isComplete) {
    await axios
      .delete(`/stories/${storyId}`)
      .then(() => {
        dispatch(removeStoryFromStore(storyId));
      })
      .catch((err) => {
        dispatch(notifyError("ups! failed to delete story"));
        Sentry.withScope((scope) => {
          scope.setTag("storyId", storyId);
          scope.setTag("method", "deleteFileUpload");
          Sentry.captureException(err);
        });
      });
    return;
  }
  // if story did not begin to upload to the cloud we just remove it from redux
  if (isNotStarted) {
    dispatch(removeStoryFromStore(storyId));
    return;
  }

  // at this point the story already began to upload, so we activate the flag to remove it when request is cancel
  dispatch(addFlagToDeleteLocalFile(storyId));
};

export const checkUploadingStatus = () => async (dispatch) => {
  const uploadingFiles = JSON.parse(localStorage.getItem("uploadingFiles"));

  try {
    const baseUrl = "/stories/status_bulk_upload";

    if (uploadingFiles) {
      const res = await axios.post(baseUrl, {
        stories: uploadingFiles.stories.map((story) => ({ _id: story.storyId })),
      });
      if (res.data.length > 0) {
        const filesCompleted = res.data?.filter((story) => story.available === true);
        const uploading = res.data?.some((story) => story.available === false && !story.failed);
        const newStories = res.data.map((story) => ({
          available: story.available,
          failed: story.failed,
          complete: story.available,
          ...story.story,
          _id: story.id,
          storyId: story.id,
        }));

        const failedStories = res.data?.filter((story) => story.failed === true);
        if (failedStories.length > 0) {
          dispatch(notifyError(`${failedStories.length} upload(s) failed.`));
        }

        if (!uploading) {
          dispatch({
            type: PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
            payload: {
              uploadingFiles: false,
              filesCompleted: filesCompleted.length > 0 ? filesCompleted.length : 0,
              updatedStories: newStories,
              total: uploadingFiles.stories.length,
              projectId: uploadingFiles.projectId,
            },
          });

          localStorage.removeItem("uploadingFiles");
          if (failedStories.length === 0) {
            dispatch(notifySuccess("all files upload correctly!"));
          }
        } else {
          dispatch({
            type: PROJECT_UPLOADING_SET_PROGRESS_CLOUD_FILES,
            payload: {
              uploadingFiles: uploading,
              filesCompleted: filesCompleted.length > 0 ? filesCompleted.length : 0,
              total: uploadingFiles.stories.length,
              updatedStories: newStories,
              projectId: uploadingFiles.projectId,
            },
          });
        }
      } else {
        localStorage.removeItem("uploadingFiles");
      }
    }
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setExtra("uploadingFiles", uploadingFiles);
      scope.setTag("method", "checkUploadingStatus");
      Sentry.captureException(error);
    });
    throw error;
  }
};

export const bulkUpdateStories = (storiesUpdated: any) => async (dispatch, getState) => {
  const { stories } = getState().uploadFiles;

  const baseUrl = "/stories/update_bulk_upload";
  // We combine the stories data from the updateBulk form in the frontend with the ones in redux
  const combinedStories = stories.map((story) => {
    const storyUpdated = storiesUpdated.find((storyToUpdate) => storyToUpdate._id === story._id);
    const merged = storyUpdated ? { ...story, ...storyUpdated } : story;
    const incomingTitle = typeof storyUpdated?.title === "string" ? storyUpdated.title.trim() : "";
    const existingTitle = typeof story.title === "string" ? story.title.trim() : "";
    if (storyUpdated && !incomingTitle && existingTitle) {
      merged.title = story.title;
    }
    return merged;
  });

  await axios
    .put(baseUrl, { stories: combinedStories })
    .then(() => {
      dispatch(notifySuccess("Story updated successfully"));
    })
    .catch((err) => {
      dispatch(notifyError("Failed to update bulk stories"));
      Sentry.withScope((scope) => {
        scope.setExtra("storiesUpdated", storiesUpdated);
        scope.setExtra("combinedStories", combinedStories);
        scope.setTag("method", "bulkUpdateStories");
        Sentry.captureException(err);
      });
    });

  dispatch({
    type: PROJECT_UPLOADING_BULK_UPDATE_FILES,
    payload: { stories: combinedStories },
  });
};

export const hydrateUploadStoriesMetadata = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (!state.featureFlags?.flags?.enableMetadataSchema) {
    return;
  }

  const { stories } = state.uploadFiles;
  if (!stories.length) {
    return;
  }

  const hydratedStories = await Promise.all(
    stories.map(async (story) => {
      const storyId = story._id || story.storyId;
      if (!storyId) {
        return story;
      }

      try {
        const { data } = await axios.get<InterviewMetadataState>(`/stories/${storyId}/interview-metadata`);
        if (!data?.schema || !data.values) {
          return story;
        }

        return {
          ...story,
          metadata_values: extractCustomMetadataValues(data.schema, data.values),
        };
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setExtra("storyId", storyId);
          scope.setTag("method", "hydrateUploadStoriesMetadata");
          Sentry.captureException(err);
        });
        return story;
      }
    })
  );

  dispatch({
    type: PROJECT_UPLOADING_HYDRATE_METADATA,
    payload: { stories: hydratedStories },
  });
};

export const bulkCreateCloudStories =
  (
    stories: any,
    projectId: string,
    accessToken: string,
    folders: any,
    provider: "box" | "google" = "box",
    folderId?: string
  ) =>
  async (dispatch) => {
    const baseUrl = provider === "google" ? "/stories/upload_bulk_google" : "/stories/upload_bulk_box";
    const tokenKey = provider === "google" ? "googleToken" : "boxToken";
    try {
      await axios
        .post(baseUrl, { stories: stories, project: projectId, [tokenKey]: accessToken, folders: folders })
        .then((res) => {
          const storiesToUpload = res.data.map((story) => ({
            _id: story._id,
            storyId: story._id,
            title: story.title,
            recordDate: story.record_date,
            type: "cloud",
          }));
          const storiesForLocalStorage = res.data.map((story) => ({ storyId: story._id, type: "cloud" }));
          localStorage.setItem(
            "uploadingFiles",
            JSON.stringify({ stories: storiesForLocalStorage, projectId, folderId })
          );

          dispatch({
            type: PROJECT_UPLOADING_SET_FILES,
            payload: { filesToUpload: storiesToUpload, projectId, total: stories.length, folders, folderId },
          });
        });
    } catch (err) {
      dispatch(notifyError(`Failed to create bulk stories`));
      Sentry.withScope((scope) => {
        scope.setExtra("stories", stories);
        scope.setTag("projectId", projectId);
        scope.setExtra("folders", folders);
        scope.setExtra("stories", stories);
        scope.setTag("method", "bulkCreateCloudStories");
        Sentry.captureException(err);
      });
    }
  };

export const needReencoding = () => async (dispatch, getState) => {
  dispatch({
    type: PROJECT_UPLOADING_LOCAL_FILES,
    // type: PROJECT_UPLOADING_ENCODING_LOCAL_FILES,
    payload: {
      ...getState().uploadLocalFiles,
      localUploadProgress: {
        ...getState().uploadLocalFiles.localUploadProgress,
        needReEncoding: true,
      },
    },
  });
};

export const setFailedLocalUploadingStatus = () => async (dispatch, getState) => {
  dispatch({
    type: PROJECT_UPLOADING_LOCAL_FILES,
    // type: PROJECT_UPLOADING_ENCODING_LOCAL_FILES,
    payload: {
      ...getState().uploadLocalFiles,
      localUploadProgress: {
        ...getState().uploadLocalFiles.localUploadProgress,
        uploadProgress: "UPLOAD_FAILED",
        uploadedPartCount: -1,
      },
    },
  });
};

export const loadStoryFiles = (storyId: string) => async () => {
  const url = `/stories/${storyId}/list_files`;

  const { data } = await axios.get<StoryFile[]>(url, {
    headers: { "Content-Type": "application/json" },
  });

  return data;
};

export const getFiles = (storyId: string) => async (dispatch) => {
  try {
    const files = await dispatch(loadStoryFiles(storyId));
    dispatch({
      type: GET_UPDATE_FILES,
      payload: { files },
    });
    return files;
  } catch (err) {
    dispatch(notifyError(`Failed while retrieving files`));
    return [];
  }
};

export const addFile = (storyId: string, payload: any) => async (dispatch) => {
  const url = `/stories/${storyId}/add_file`;
  try {
    const response = await axios.post(url, payload).then((res) => {
      dispatch({
        type: ADD_FILE,
        payload: { file: res.data },
      });
      dispatch(notifySuccess("File uploaded successfully."));
    });
    return response;
  } catch (error) {
    dispatch(notifyError("An error was encountered while file upload."));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setTag("method", "handleFileUpload");
      Sentry.captureException(error);
    });
    console.error("Failed to upload file: ", error);
    return error;
  }
};

export const updateFileName = (_id, title, description, altText, credit) => (dispatch, getState) => {
  const files = getState().uploadFiles.filesTab;

  const fileToUpdate = files.find((file) => file._id === _id);
  const fileUpdated = { ...fileToUpdate, title: title, description: description, altText: altText, credit: credit };
  const filteredFiles = files.filter((file) => file._id !== _id);
  const filesUpdated = [...filteredFiles, fileUpdated];

  dispatch({
    type: GET_UPDATE_FILES,
    payload: {
      files: filesUpdated,
    },
  });
};

export const updateFileReleaseForm = (_id, releaseForm, storyId) => async (dispatch, getState) => {
  const files = getState().uploadFiles.filesTab;
  const url = `/stories/${storyId}/edit_file/${_id}`;
  const payload = {
    release_form: !releaseForm,
  };

  const filteredFiles = files.filter((file) => file._id !== _id);
  const fileToUpdate = files.find((file) => file._id === _id);
  const fileUpdated = { ...fileToUpdate, release_form: !releaseForm };
  const filesUpdated = [...filteredFiles, fileUpdated];

  try {
    const response = await axios.put(url, payload, {
      headers: { "Content-Type": "application/json" },
      params: { release_form: !releaseForm },
    });

    if (response.status === 200) {
      dispatch({
        type: GET_UPDATE_FILES,
        payload: {
          files: filesUpdated,
        },
      });
    }
    dispatch(notifySuccess("File updated successfully."));
  } catch (error) {
    dispatch(notifyError("An error was encountered while updating the file."));
    Sentry.withScope((scope) => {
      scope.setTag("storyId", storyId);
      scope.setExtra("payload", payload);
      scope.setTag("method", "setAsReleaseForm");
      Sentry.captureException(error);
    });
    console.error("Failed to upload file: ", error);
  }
};
